import React from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import throttle from 'lodash/throttle';
import { useRecoilState } from 'recoil';
import SearchIcon from '@mui/icons-material/Search';
import { OrderPayload } from 'pages/myidm/store';
import { CssTextField } from 'components/select';
import { REQUEST_TYPE } from 'pages/myidm/utils/constant';

const AdGroupMember = ({ label, index, localMembers, isSecondaryLoading }) => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [value, setValue] = React.useState(null);
    const [ccInputValue, setCCInputValue] = React.useState('-');
    const [options, setOptions] = React.useState([]);
    const [searched, setSearched] = React.useState(false);
    const adGrpMemberApi = useMutation(() => axios.get(`${endPoints.appProfile.appOwner}?key=${ccInputValue}`));
    const crossDomainUserApi = useMutation((payload) => axios.get(`${endPoints?.myIdm?.isCrossDomainUser}/${payload?.gpid}`));
    const validateAdGrpMember = (input, crossDomainFlag) => {
        const isPresent = localMembers?.filter((element) => element?.gpid === input?.gpid);

        if (!input) {
            return { isValid: false, message: null };
        }

        const isAlreadyExistingMember =
            (order?.adGroupMembers?.length > 0 && order?.adGroupMembers?.filter((member) => member?.gpid?.toLowerCase() === input?.gpid?.toLowerCase())) || [];

        if (crossDomainFlag === true) {
            return {
                isValid: false,
                message:
                    'Given member belongs to CWW domain, for Non-Universal AD group we can only add CORPPVT members hence please choose another member if you want to proceed further',
            };
        }

        if (isAlreadyExistingMember?.length > 0) {
            return {
                isValid: false,
                message: 'Member already present in the AD Group and please choose different member if you wish to continue further.',
            };
        }

        if (isPresent?.length > 0) {
            return { isValid: false, message: 'Member already added in the list.' };
        }

        if (isPresent?.length === 0 && input && isAlreadyExistingMember?.length === 0) {
            return { isValid: true, message: '' };
        }

        return { isValid: true, message: '' };
    };

    const setAdGroupMember = async (adGrpMember, adGrpMemberDetails) => {
        let crossDomainFlag = false;
        // Get isUniversalADGroup from the correct multipleAdGroups index if available
        const isUniversalADGroup = Array.isArray(order?.multipleAdGroups) && order?.multipleAdGroups[index]?.isUniversalADGroup;
        // CWW domain groups already allow CWW members, so the cross-domain check doesn't apply
        const isCwwDomain = order?.domainType?.toLowerCase()?.includes('cww');
        if (isUniversalADGroup === false && adGrpMemberDetails?.gpid && order?.typeOfRequest === REQUEST_TYPE.MODIFY && !isCwwDomain) {
            try {
                const response = await crossDomainUserApi.mutateAsync({ gpid: adGrpMemberDetails.gpid });
                crossDomainFlag = response?.data?.isCrossDomainUser || false;
            } catch (e) {
                crossDomainFlag = false;
            }
        }

        // Validate the member
        const validation = validateAdGrpMember(adGrpMemberDetails, crossDomainFlag);

        setOrder({
            ...order,
            adGrpMember,
            adGrpMemberDetails,
            crossDomainUser: crossDomainFlag,
            adGrpMemberValidation: validation,
        });
    };

    React.useEffect(() => {
        if (!order?.adGrpMember && !order?.adGrpMemberDetails) {
            setOptions([]);
            // Also reset the input value when order.adGrpMember is cleared externally
            if (ccInputValue !== '-') {
                setCCInputValue('-');
                setValue(null);
            }
        }
    }, [order?.adGrpMember, order?.adGrpMemberDetails]);

    // Clear serviceAccount when user types in adGrpMember field
    React.useEffect(() => {
        if (ccInputValue && ccInputValue !== '-') {
            // Always clear serviceAccount fields when typing in adGrpMember
            setOrder((prevOrder) => {
                // Only update if serviceAccount fields actually have values to avoid unnecessary rerenders
                if (prevOrder?.serviceAccount || prevOrder?.serviceAccountValidation?.message) {
                    return {
                        ...prevOrder,
                        serviceAccount: '',
                        serviceAccountValidation: { isValid: true, message: '' },
                    };
                }
                return prevOrder;
            });
        }
    }, [ccInputValue]);

    // Revalidate when localMembers or adGroupMembers changes
    React.useEffect(() => {
        if (order?.adGrpMemberDetails) {
            const validation = validateAdGrpMember(order?.adGrpMemberDetails, order?.crossDomainUser);
            if (validation.isValid !== order?.adGrpMemberValidation?.isValid || validation.message !== order?.adGrpMemberValidation?.message) {
                setOrder((prevOrder) => ({
                    ...prevOrder,
                    adGrpMemberValidation: validation,
                }));
            }
        }
    }, [localMembers, order?.adGroupMembers]);

    const resetOptions = () => {
        setOptions([]);
        setAdGroupMember(null, null);
    };
    const fetch = React.useMemo(
        () =>
            throttle(() => {
                adGrpMemberApi.mutate();
            }, 1000),
        []
    );
    const handleSearch = () => {
        // istanbul ignore if
        if (ccInputValue === '') {
            resetOptions();
        }
        // istanbul ignore else
        if (ccInputValue !== '' && ccInputValue.length > 2) fetch();
    };
    React.useEffect(() => {
        if (adGrpMemberApi?.data?.data) {
            const combinedValue = adGrpMemberApi?.data?.data?.map((app) => ({
                label: `${app.gpid} - ${app.name}`,
                gpid: `${app.gpid}`,
                name: `${app.name}`,
                email: `${app.email}`,
            }));
            setOptions(combinedValue);
        }
    }, [adGrpMemberApi?.data?.data]);

    React.useEffect(() => {
        if (value === null) {
            // resetOptions();
        } else {
            setOptions(options?.filter((id) => id?.gpid === value?.gpid));
            setAdGroupMember(value?.label, value);
        }
    }, [value]);

    React.useEffect(() => {
        if (!order?.adGrpMember && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [order?.adGrpMember, order?.isTouched]);

    const validateInput = () => {
        if ((isTouched && order?.isTouched && !order?.adGrpMember) || isTouched) {
            return 'validate';
        }
        return '';
    };

    const getNoOptionText = () => {
        let text = '';
        if (searched === false) {
            text = 'Type & press enter to search by GPID, Firstname and Lastname';
        } else if (options?.length === 0) {
            text = 'No results found';
        }
        return text;
    };
    const isLoading = adGrpMemberApi?.isLoading || crossDomainUserApi?.isLoading || isSecondaryLoading;
    const showError = !order?.adGrpMemberValidation?.isValid && order?.adGrpMemberValidation?.message;

    return (
        <>
            <Box display="flex" alignItems="center" gap={1} data-testid="app-owner-box" width="100%">
                <Autocomplete
                    data-testid="app-owner-autocomplete"
                    key="adGrpMemberDetails"
                    size="small"
                    loading={adGrpMemberApi?.isLoading}
                    noOptionsText={getNoOptionText()}
                    value={order?.adGrpMember || null}
                    onInputChange={(event, newInputValue) => {
                        if (newInputValue) setCCInputValue(newInputValue?.includes('-') ? newInputValue?.split('-')?.pop()?.trim() : newInputValue);
                    }}
                    className={validateInput()}
                    onChange={(event, newValue) => {
                        // istanbul ignore else
                        if (typeof newValue === 'string') {
                            setValue({
                                label: newValue,
                            });
                        } else if (newValue && newValue.inputValue) {
                            // Create a new value from the user input
                            setValue({
                                label: newValue.inputValue,
                            });
                        } else {
                            setValue(newValue);
                        }
                    }}
                    onBlur={() => {
                        if (!value || !order?.adGrpMember) {
                            setSearched(false);
                            setOptions([]);
                        }
                    }}
                    onKeyUp={(event) => {
                        if (event.key === 'Enter') {
                            setSearched(false);
                            handleSearch();
                        }
                        if ((event.key === 'Backspace' || event.key === 'Delete') && (!event.target.value || event.target.value === '')) {
                            setSearched(false);
                            resetOptions();
                        }
                    }}
                    //    filterOptions={filterOptions}

                    filterOptions={(optionValues, params) => {
                        // eslint-disable-next-line no-underscore-dangle
                        const _results = [];
                        // eslint-disable-next-line no-plusplus
                        for (let i = 0; i < optionValues.length; i++) {
                            // eslint-disable-next-line no-restricted-syntax
                            for (const key in optionValues[i]) {
                                if (optionValues[i][key] !== null && typeof optionValues[i][key] !== 'boolean' && typeof optionValues[i][key] !== 'number') {
                                    if (optionValues[i][key].toLowerCase().includes(params?.inputValue.toLowerCase())) {
                                        _results.push(optionValues[i]);
                                    }
                                }
                            }
                        }
                        return [...new Set(_results)];
                        // return _results
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    disableClearable
                    id="free-solo-with-text-demo"
                    options={options}
                    sx={{ width: '100%' }}
                    renderInput={(params) => (
                        <CssTextField
                            size="large"
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...params}
                            label={label}
                            error={showError}
                            helperText={showError ? order?.adGrpMemberValidation?.message : ''}
                            sx={{
                                width: (theme) => theme.spacing('100%'),
                                '& .MuiOutlinedInput-root': {
                                    paddingRight: '14px !important', // Override the automatic padding
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoading ? (
                                            <CircularProgress data-testid="loader" color="black" size={16} />
                                        ) : (
                                            <SearchIcon
                                                sx={{ cursor: 'pointer', color: '#0047BA' }}
                                                onClick={() => {
                                                    handleSearch();
                                                    setSearched(true);
                                                }}
                                                data-testid="search-btn"
                                            />
                                        )}
                                    </>
                                ),
                            }}
                            fullWidth
                        />
                    )}
                />
            </Box>
        </>
    );
};

AdGroupMember.propTypes = {
    index: PropTypes.number.isRequired,
    label: PropTypes.string,
    localMembers: PropTypes.array,
    isSecondaryLoading: PropTypes.bool,
};

AdGroupMember.defaultProps = {
    label: 'AD Group member',
    localMembers: [],
    isSecondaryLoading: false,
};

export default React.memo(AdGroupMember);
